import { trackButtonClicked } from '@/lib/analytics/scribe/button';
import { trackScreenViewed } from '@/lib/analytics/scribe/ui';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type {
  Heidi2OnboardingAnalyticsStep,
  Heidi2OnboardingVariant,
} from '@/models/analytics/features/onboarding';
import type {
  Heidi2OnboardingButtonLabel,
  Heidi2OnboardingExit,
  Heidi2OnboardingReportedStep,
  Heidi2OnboardingStep,
} from '@heidi/agents';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';

/**
 * `first_run`: the admin whose tour activates the workspace. `activated`: a
 * first run on a team already on Heidi II. `relaunch`: reopened part-way in.
 */
export type Heidi2OnboardingSource = 'first_run' | 'activated' | 'relaunch';

const ONBOARDING_VARIANT: Heidi2OnboardingVariant = 'heidi_2_agents';

const ANALYTICS_STEP = {
  welcome: 'welcome',
  handoff: 'assign_tasks',
  'tell-heidi': 'tell_heidi',
  'handles-it-first-prompt': 'heidi_works_1',
  'handles-it-follow-up': 'heidi_works_2',
  'handles-it-approval': 'final_approval',
} satisfies Record<Heidi2OnboardingReportedStep, Heidi2OnboardingAnalyticsStep>;

/** The screen someone left the tour from, sent as `source` on a Skip tour. */
const EXIT_SOURCE = {
  welcome: 'welcome',
  handoff: 'assign_tasks',
  'tell-heidi': 'tell_heidi',
  'handles-it': 'heidi_works',
  ready: 'ready',
} satisfies Record<Heidi2OnboardingStep, string>;

const STEP_SCREEN = {
  welcome: ScreenName.Heidi2OnboardingWelcome,
  handoff: ScreenName.Heidi2OnboardingHandoff,
  'tell-heidi': ScreenName.Heidi2OnboardingTellHeidi,
  'handles-it': ScreenName.Heidi2OnboardingHandlesIt,
  ready: ScreenName.Heidi2OnboardingReady,
} satisfies Record<Heidi2OnboardingStep, ScreenName>;

export const trackHeidi2OnboardingScreenViewed = (step: Heidi2OnboardingStep) =>
  track({
    event_name: TrackingEventName.ScreenViewed,
    props: { screen_name: STEP_SCREEN[step] },
    options: { amplitude: false, snowplow: true },
  });

/**
 * The takeover mounts with the first step, so the tour opening IS the screen
 * view. Left on default routing: `screen_viewed` is Snowplow-suppressed, so
 * Amplitude is the only destination that would receive it.
 */
export const trackHeidi2IntroViewed = (ctaCopy?: string) =>
  trackScreenViewed({
    screen_name: ScreenName.IntroHeidi2,
    ...(ctaCopy && { onboarding_sub_variant: ctaCopy }),
  });

export const trackHeidi2OnboardingStarted = (source: Heidi2OnboardingSource) =>
  track({
    event_name: TrackingEventName.OnboardingStarted,
    props: { onboarding_variant: ONBOARDING_VARIANT, source },
    options: { amplitude: false },
  });

/** `label` carries the flag-resolved copy so the arm is joinable to the view. */
const trackHeidi2IntroCtaClicked = (ctaCopy?: string) =>
  trackButtonClicked({
    screen_name: ScreenName.IntroHeidi2,
    action: ButtonAction.IntroHeidi2,
    label: ctaCopy ?? 'see_how_it_works',
  });

export const trackHeidi2OnboardingButtonClicked = (
  label: Heidi2OnboardingButtonLabel,
  step: Heidi2OnboardingStep
) =>
  trackButtonClicked({
    screen_name: ScreenName.Heidi2AgentsOnboarding,
    action: ButtonAction.Heidi2AgentsOnboardingInteraction,
    label,
    step_name: EXIT_SOURCE[step],
  });

export const trackHeidi2OnboardingStepCompleted = (
  step: Heidi2OnboardingReportedStep,
  stepNumber: number
) =>
  track({
    event_name: TrackingEventName.OnboardingStepCompleted,
    props: {
      onboarding_variant: ONBOARDING_VARIANT,
      step: ANALYTICS_STEP[step],
      step_number: stepNumber,
    },
    options: { amplitude: false },
  });

export const trackHeidi2OnboardingStepSkipped = (step: Heidi2OnboardingReportedStep) =>
  track({
    event_name: TrackingEventName.OnboardingStepSkipped,
    props: { onboarding_variant: ONBOARDING_VARIANT, step_name: ANALYTICS_STEP[step] },
    options: { amplitude: false },
  });

const trackHeidi2OnboardingTourSkipped = (step: Heidi2OnboardingStep) =>
  track({
    event_name: TrackingEventName.OnboardingStepSkipped,
    props: {
      onboarding_variant: ONBOARDING_VARIANT,
      step_name: 'skip_tour',
      source: EXIT_SOURCE[step],
    },
    options: { amplitude: false },
  });

const trackHeidi2OnboardingDismissed = (label: 'explore_later' | 'close') =>
  trackButtonClicked({
    screen_name: ScreenName.Heidi2AgentsOnboarding,
    action: ButtonAction.Heidi2AgentsOnboardingDismiss,
    label,
  });

const TOUR_LEFT_TRACKERS = {
  'skip-tour': trackHeidi2OnboardingTourSkipped,
  'explore-later': () => trackHeidi2OnboardingDismissed('explore_later'),
  close: () => trackHeidi2OnboardingDismissed('close'),
} satisfies Record<Heidi2OnboardingExit, (step: Heidi2OnboardingStep) => void>;

/**
 * Skip tour counts as a skipped step, as the design asks. Explore later and
 * closing the card count as dismissing the tour.
 */
export const trackHeidi2OnboardingLeft = (
  exit: Heidi2OnboardingExit,
  step: Heidi2OnboardingStep
) => TOUR_LEFT_TRACKERS[exit](step);

export const trackHeidi2OnboardingBookDemoOpened = () =>
  trackButtonClicked({
    screen_name: ScreenName.Heidi2AgentsOnboarding,
    action: ButtonAction.Heidi2AgentsOnboardingBookDemo,
    label: 'book_demo',
  });

export const trackHeidi2OnboardingCompleted = (source: Heidi2OnboardingSource) =>
  track({
    event_name: TrackingEventName.OnboardingCompleted,
    props: { onboarding_variant: ONBOARDING_VARIANT, source },
    options: { amplitude: false },
  });

/** Every tour tracker, keyed by the flow prop it plugs into. */
export const heidi2OnboardingTrackers = (
  source: Heidi2OnboardingSource,
  /** Flag-resolved welcome CTA copy; absent off the experiment. */
  ctaCopy?: string
) => ({
  onStepView: (step: Heidi2OnboardingStep) => trackHeidi2OnboardingScreenViewed(step),
  onTourStarted: () => {
    trackHeidi2IntroViewed(ctaCopy);
    trackHeidi2OnboardingStarted(source);
  },
  onStepCompleted: (step: Heidi2OnboardingReportedStep, stepNumber: number) => {
    if (step === 'welcome') trackHeidi2IntroCtaClicked(ctaCopy);
    trackHeidi2OnboardingStepCompleted(step, stepNumber);
  },
  onStepSkipped: trackHeidi2OnboardingStepSkipped,
  onTourLeft: trackHeidi2OnboardingLeft,
  onBookDemoOpen: trackHeidi2OnboardingBookDemoOpened,
  onButtonClick: trackHeidi2OnboardingButtonClicked,
  onTourCompleted: () => trackHeidi2OnboardingCompleted(source),
});
